import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import type { RunProjection, WorkflowDefinition, WorkflowInput } from "./types";
export const workflowUrl = "/collaboration/approval-workflows";
export const workflowQueryKey = ["approval-workflows"] as const;
export function useWorkflowDefinitions(enabled: boolean, tenantId?: string) {
  return useQuery({
    queryKey: [...workflowQueryKey, tenantId],
    enabled,
    queryFn: async () =>
      (await axios.get<{ definitions: WorkflowDefinition[] }>(workflowUrl)).data
        .definitions,
  });
}
export async function saveWorkflow(
  definition: WorkflowInput,
  previous?: WorkflowDefinition
) {
  const {
    name,
    enabled,
    entryStatusKey,
    scope,
    steps,
    approvedStatusKey,
    rejectedStatusKey,
    layout,
  } = definition;
  const payload: WorkflowInput = {
    name,
    enabled,
    entryStatusKey,
    scope,
    steps,
    approvedStatusKey,
    rejectedStatusKey,
    layout,
  };
  const response = previous
    ? await axios.put<{ definition: WorkflowDefinition }>(
        `${workflowUrl}/${encodeURIComponent(previous.id)}`,
        { expectedVersion: previous.version, definition: payload }
      )
    : await axios.post<{ definition: WorkflowDefinition }>(
        workflowUrl,
        payload
      );
  return response.data.definition;
}
export async function archiveWorkflow(definition: WorkflowDefinition) {
  return (
    await axios.post<{ definition: WorkflowDefinition }>(
      `${workflowUrl}/${encodeURIComponent(definition.id)}/archive`,
      { expectedVersion: definition.version }
    )
  ).data.definition;
}
export function workflowError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message;
    if (typeof message === "string" && message.trim()) return message;
    if (error.response?.status === 409)
      return "This workflow changed or conflicts with an active run. Reload the latest version and review your changes.";
    const detail = error.response?.data?.error;
    if (typeof detail === "string") return detail;
  }
  return error instanceof Error
    ? error.message
    : "Unable to save. Please try again.";
}
export async function queryRuns(postIds: string[]) {
  return (
    await axios.post<{ runs: RunProjection[] }>(`${workflowUrl}/runs/query`, {
      postIds,
    })
  ).data.runs;
}

// Cards mount together. Coalesce their reads into the contract's bounded batch
// while React Query continues to cache/refetch each post independently.
type RunWaiter = {
  resolve: (run: RunProjection | null) => void;
  reject: (error: unknown) => void;
};
const pendingRunReads = new Map<string, Map<string, RunWaiter[]>>();
export function queryRunForPost(
  tenantId: string,
  postId: string
): Promise<RunProjection | null> {
  return new Promise((resolve, reject) => {
    let batch = pendingRunReads.get(tenantId);
    if (!batch) {
      batch = new Map();
      pendingRunReads.set(tenantId, batch);
      setTimeout(() => {
        const ready = pendingRunReads.get(tenantId);
        pendingRunReads.delete(tenantId);
        if (!ready) return;
        const ids = [...ready.keys()];
        for (let offset = 0; offset < ids.length; offset += 100) {
          const chunk = ids.slice(offset, offset + 100);
          void queryRuns(chunk).then(
            (runs) => {
              const byPost = new Map(runs.map((run) => [run.postId, run]));
              for (const id of chunk)
                for (const waiter of ready.get(id) ?? [])
                  waiter.resolve(byPost.get(id) ?? null);
            },
            (error) => {
              for (const id of chunk)
                for (const waiter of ready.get(id) ?? []) waiter.reject(error);
            }
          );
        }
      }, 0);
    }
    batch.set(postId, [...(batch.get(postId) ?? []), { resolve, reject }]);
  });
}
